import { useState, useEffect } from 'react';
import { Flame, Plus, Clock, Thermometer, Gauge, Package, Check, Play, RefreshCw, FileText, Upload } from 'lucide-react';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/apiClient';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { AutoclaveCycleDialog } from '@/components/Operator/AutoclaveCycleDialog';
import { AutoclaveCycleLoader } from '@/components/Autoclave/AutoclaveCycleLoader';
import { Nfc } from 'lucide-react';

interface AutoclaveCycle {
  id: string;
  machine_id: string;
  machine_name?: string;
  recipe_id?: string | null;
  recipe_name?: string | null;
  cycle_number: string | null;
  pressure: number | null;
  temperature: number | null;
  duration_minutes: number | null;
  actual_duration_minutes: number | null;
  operator_id: string | null;
  operator_name?: string;
  started_at: string | null;
  completed_at: string | null;
  status: string;
  notes: string | null;
  created_at: string;
  pieces_count?: number;
}

interface Machine {
  id: string;
  name: string;
}

export default function AutoclaveCycles() {
  const [cycles, setCycles] = useState<AutoclaveCycle[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMachine, setSelectedMachine] = useState<string>('all');
  const [showCycleDialog, setShowCycleDialog] = useState(false);
  const [selectedCycleId, setSelectedCycleId] = useState<string | undefined>();
  const [showLoader, setShowLoader] = useState(false);
  const [showLogUpload, setShowLogUpload] = useState(false);
  const [selectedCycleForLog, setSelectedCycleForLog] = useState<AutoclaveCycle | null>(null);
  const [uploadingLog, setUploadingLog] = useState(false);
  const [cycleLogs, setCycleLogs] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchMachines();
    fetchCycles();
  }, []);

  useEffect(() => {
    fetchCycles();
  }, [selectedMachine]);
  // Cargar conteo de logs por ciclo
  const fetchLogCounts = async () => {
    try {
      const { data, error } = await api
        .from('autoclave_logs')
        .select('cycle_id');

      if (error) throw error;

      const counts: Record<string, number> = {};
      (data || []).forEach((log: any) => {
        if (log.cycle_id) {
          counts[log.cycle_id] = (counts[log.cycle_id] || 0) + 1;
        }
      });
      setCycleLogs(counts);
    } catch (error) {
      console.error('Error fetching log counts:', error);
    }
  };

  useEffect(() => {
    fetchLogCounts();
  }, [cycles]);

  // Subir log de autoclave
  const handleUploadLog = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCycleForLog) return;

    setUploadingLog(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('machine_id', selectedCycleForLog.machine_id);
      formData.append('cycle_id', selectedCycleForLog.id);

      const response = await fetch('/api/autoclave-logs/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Error uploading');

      const result = await response.json();

      // Insertar en BD via PostgREST
      const { error } = await api
        .from('autoclave_logs')
        .insert(result.data);

      if (error) throw error;

      toast.success('Log subido correctamente');
      setShowLogUpload(false);
      setSelectedCycleForLog(null);
      fetchLogCounts();
    } catch (error) {
      console.error('Error uploading log:', error);
      toast.error('Error al subir el log');
    } finally {
      setUploadingLog(false);
    }
  };

  const openLogUpload = (cycle: AutoclaveCycle, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCycleForLog(cycle);
    setShowLogUpload(true);
  };


  const fetchMachines = async () => {
    try {
      const { data, error } = await api
        .from('machines')
        .select('id, name')
        .eq('machine_type', 'autoclave')
        .eq('is_active', true);

      if (error) throw error;
      setMachines(data || []);
    } catch (error) {
      logger.error('Error fetching machines:', error);
    }
  };

  const fetchCycles = async () => {
    try {
      setLoading(true);

      let query = api
        .from('autoclave_cycles')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (selectedMachine !== 'all') {
        query = query.eq('machine_id', selectedMachine);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Enrich with machine names, operator names, and piece counts
      const enrichedCycles = await Promise.all((data || []).map(async (cycle) => {
        // Get machine name
        const machine = machines.find(m => m.id === cycle.machine_id);

        // Get operator name
        let operator_name = null;
        if (cycle.operator_id) {
          const { data: profile } = await api
            .from('profiles')
            .select('full_name')
            .eq('id', cycle.operator_id)
            .limit(1);
          operator_name = profile?.[0]?.full_name;
        }

        // Get pieces count
        const { data: pieces } = await api
          .from('autoclave_cycle_pieces')
          .select('id')
          .eq('cycle_id', cycle.id);

        // Get recipe name (consulta directa; el embed no resuelve por FKs del esquema)
        let recipe_name = null;
        if (cycle.recipe_id) {
          const { data: rec } = await api
            .from('production_recipes')
            .select('name')
            .eq('id', cycle.recipe_id)
            .limit(1);
          recipe_name = rec?.[0]?.name || null;
        }

        return {
          ...cycle,
          machine_name: machine?.name || 'Desconocido',
          recipe_name,
          operator_name,
          pieces_count: pieces?.length || 0
        };
      }));

      setCycles(enrichedCycles);
    } catch (error) {
      logger.error('Error fetching cycles:', error);
      toast.error('Error al cargar los ciclos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const config: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pendiente', className: 'bg-gray-100 text-gray-700' },
      in_progress: { label: 'En Progreso', className: 'bg-orange-100 text-orange-700' },
      completed: { label: 'Completado', className: 'bg-green-100 text-green-700' }
    };
    const c = config[status] || config.pending;
    return <Badge variant="outline" className={c.className}>{c.label}</Badge>;
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return format(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: es });
    } catch {
      return '—';
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return '—';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const handleNewCycle = (machineId?: string) => {
    setSelectedCycleId(undefined);
    setShowCycleDialog(true);
  };

  const handleViewCycle = (cycleId: string) => {
    setSelectedCycleId(cycleId);
    setShowCycleDialog(true);
  };

  // Stats
  const activeCycles = cycles.filter(c => c.status === 'in_progress').length;
  const completedToday = cycles.filter(c => {
    if (!c.completed_at) return false;
    const today = new Date().toDateString();
    return new Date(c.completed_at).toDateString() === today;
  }).length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Flame className="h-6 w-6 text-orange-500" />
              Ciclos de Autoclave
            </h1>
            <p className="text-muted-foreground">
              Gestiona los ciclos de curado en autoclaves
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowLoader(true)} variant="outline">
              <Nfc className="h-4 w-4 mr-2" />
              Cargar ciclo (NFC)
            </Button>
            <Button onClick={() => handleNewCycle()} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Ciclo
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-orange-100">
                  <Play className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{activeCycles}</p>
                  <p className="text-sm text-muted-foreground">Ciclos Activos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <Check className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{completedToday}</p>
                  <p className="text-sm text-muted-foreground">Completados Hoy</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <Flame className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{machines.length}</p>
                  <p className="text-sm text-muted-foreground">Autoclaves</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <Select value={selectedMachine} onValueChange={setSelectedMachine}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtrar por autoclave" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los autoclaves</SelectItem>
              {machines.map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchCycles}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Cycles Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Historial de Ciclos</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Cargando ciclos...</div>
            ) : cycles.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Flame className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No hay ciclos registrados</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Autoclave</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Piezas</TableHead>
                      <TableHead>Parámetros</TableHead>
                      <TableHead>Inicio</TableHead>
                      <TableHead>Duración</TableHead>
                      <TableHead>Receta</TableHead>
                      <TableHead>Operario</TableHead>
                      <TableHead className="w-[80px] text-center">Logs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cycles.map(cycle => (
                      <TableRow
                        key={cycle.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleViewCycle(cycle.id)}
                      >
                        <TableCell className="font-medium">{cycle.machine_name}</TableCell>
                        <TableCell>{getStatusBadge(cycle.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Package className="h-4 w-4 text-muted-foreground" />
                            {cycle.pieces_count}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm space-y-0.5">
                            {cycle.pressure && (
                              <div className="flex items-center gap-1">
                                <Gauge className="h-3 w-3" />
                                {cycle.pressure} bar
                              </div>
                            )}
                            {cycle.temperature && (
                              <div className="flex items-center gap-1">
                                <Thermometer className="h-3 w-3" />
                                {cycle.temperature}°C
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDateTime(cycle.started_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 text-muted-foreground" />
                            {formatDuration(cycle.actual_duration_minutes || cycle.duration_minutes)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {cycle.recipe_name ? (
                            <span className="text-sm">{cycle.recipe_name}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">Manual</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {cycle.operator_name || '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={(e) => openLogUpload(cycle, e)}
                            title={cycleLogs[cycle.id] ? `${cycleLogs[cycle.id]} log(s)` : 'Subir log'}
                          >
                            {cycleLogs[cycle.id] ? (
                              <Badge variant="secondary" className="h-6 w-6 p-0 flex items-center justify-center">
                                {cycleLogs[cycle.id]}
                              </Badge>
                            ) : (
                              <Upload className="h-4 w-4 text-muted-foreground" />
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cycle Dialog */}
      <AutoclaveCycleDialog
        open={showCycleDialog}
        onOpenChange={setShowCycleDialog}
        cycleId={selectedCycleId}
        onCycleComplete={() => {
          fetchCycles();
          toast.success('Ciclo completado');
        }}
      />

      <AutoclaveCycleLoader
        open={showLoader}
        onOpenChange={setShowLoader}
        onSuccess={fetchCycles}
      />
    </DashboardLayout>
  );
}
